import { useEffect, useState } from "react";
import Avatar from "react-avatar";
import { Col, Collapse, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  useLazyViewPurchaseHistoriesAdminQuery,
  useLazyViewPurchaseHistoriesQuery,
} from "../../../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectProfile } from "../../../../../redux/features/user/user-slice";
import ViewPurchaseHistoriesRequestDTO from "../../../../../shared/oversight-core/dtos/request-dtos/view-purchase-histories-request-dto";
import ViewPurchaseHistoriesResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/view-purchase-histories-response-dto";
import { UserRole } from "../../../../../shared/oversight-core/enums/user-roles";
import { EViewSolarEstimationSolarProductCategory } from "../../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import { IPurchaseHistoryView } from "../../../../../shared/oversight-core/interfaces/purchase-history-view";
import { IUserProfile } from "../../../../../shared/oversight-core/interfaces/user-profile";
import { IViewSolarEstimationSolarProductAndOtherDetails } from "../../../../../shared/oversight-core/interfaces/view-solar-estimation-solar-system";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  getStatusColor,
  getStatusNameUser,
} from "../../../../../shared/oversight-core/utils/estimation-status";
import { formatWithCommas } from "../../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../../shared/oversight-core/utils/helpers";
import { convertUTCToLocal } from "../../../../../shared/oversight-core/utils/time-utils";
import PurchaseSolarComponent from "../purchase-solar-component/purchase-solar-component";
import styles from "./purchase-history-modal.module.scss";

interface IProps extends ModelContainerProps {
  show: boolean;
  userId: string;
}

interface IUserDetails {
  user: IUserProfile;
  currency: string;
}

const PurchaseHistoryModal = (props: IProps) => {
  const { show, userId, ...rest } = props;

  const profile = useSelector(selectProfile);

  const [collapsed, setCollapsed] = useState<{
    [key: string]: boolean;
  }>({});
  const [collapsedComponents, setCollapsedComponents] = useState<{
    [key: string]: boolean;
  }>({});
  const [purchaseHistories, setPurchaseHistories] = useState<
    IPurchaseHistoryView[]
  >([]);
  const [userDetails, setUserDetails] = useState<IUserDetails>();
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [lastPurchaseDate, setLastPurchaseDate] = useState("");

  const [
    triggerPurchaseHistoriesAdmin,
    { isFetching: isFetchingPurchaseHistoriesAdmin },
  ] = useLazyViewPurchaseHistoriesAdminQuery();
  const [
    triggerPurchaseHistories,
    { isFetching: isFetchingPurchaseHistories },
  ] = useLazyViewPurchaseHistoriesQuery();

  useEffect(() => {
    if (show && profile?.role) {
      const requestParams: ViewPurchaseHistoriesRequestDTO = {
        pageSize: 1000,
      };

      if (userId) {
        requestParams.userIdentity = userId;
      }

      (profile?.role === UserRole.ROLE_USER
        ? triggerPurchaseHistories
        : triggerPurchaseHistoriesAdmin)({ ...requestParams })
        .unwrap()
        .then((res: ViewPurchaseHistoriesResponseDTO) => {
          setPurchaseHistories(res.page.elements);
          setUserDetails({
            user: res.page.elements[0].user,
            currency: res.page.elements[0].solarEstimation.currency,
          });
          setTotalOrders(res.totalCount);
          setTotalAmount(res.totalAmount);
          setLastPurchaseDate(res.latestDate);
        })
        .catch(() => {
          setPurchaseHistories([]);
        });
    }
  }, [show, profile?.role, userId]);

  return (
    <ModalContainer
      {...rest}
      title={"Purchase History"}
      show={show}
      size="modal-xl"
      isButtonsVisible={false}
    >
      <>
        {purchaseHistories.length > 0 && (
          <Row
            className={`bg-white rounded-3 px-0 py-3 mx-0 row-cols-auto justify-content-between gap-2 ${styles.purchaseHistoryModalContainer}`}
          >
            <Col className="ms-2">
              <Avatar
                name={`${userDetails?.user.firstName} ${userDetails?.user.lastName}`}
                size="40"
                round={true}
              />
            </Col>
            <Col>
              <Row>
                <Col className="text-light font-weight-500 font-size-12">
                  Email
                </Col>
              </Row>
              <Row>
                <Col className="text-dark-4 font-weight-500 font-size-14">
                  {userDetails?.user.email}
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                <Col className="text-light font-weight-500 font-size-12">
                  Mobile Number
                </Col>
              </Row>
              <Row>
                <Col className="text-dark-4 font-weight-500 font-size-14">
                  {userDetails?.user.contactNumber}
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                <Col className="text-light font-weight-500 font-size-12">
                  Last Purchase
                </Col>
              </Row>
              <Row>
                <Col className="text-dark-4 font-weight-500 font-size-14">
                  {convertUTCToLocal(lastPurchaseDate, "dd/MM/yyyy")}
                </Col>
              </Row>
            </Col>
            <Col className="d-none d-lg-block">
              <Row>
                <Col className="text-light font-weight-500 font-size-12">
                  Total Orders
                </Col>
              </Row>
              <Row>
                <Col className="text-dark-4 font-weight-500 font-size-14">
                  {totalOrders.toString().padStart(2, "0")}
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                <Col className="text-light font-weight-500 font-size-12">
                  Total Spent
                </Col>
              </Row>
              <Row>
                <Col className="text-dark-4 font-weight-500 font-size-14">
                  {userDetails?.currency}&nbsp;
                  {formatWithCommas(Number(Helper.roundTo2(totalAmount)))}
                </Col>
              </Row>
            </Col>
          </Row>
        )}
        {purchaseHistories.length > 0 ? (
          purchaseHistories.map((history) => {
            return (
              <div key={history.Id}>
                <Row className="align-items-center justify-content-between mt-4">
                  <Col>
                    <Row className="align-items-start">
                      <Col className="col-auto">
                        <Row>
                          <Col className="text-primary font-weight-500 font-size-20 text-decoration-underline">
                            #{history.solarEstimation.estimationId}&nbsp;|&nbsp;
                            {history.solarEstimation.title}
                          </Col>
                        </Row>
                        <Row>
                          <Col className="col-auto text-light font-weight-400 font-size-10 pe-0">
                            {convertUTCToLocal(
                              history.confirmedAt
                                ? history.confirmedAt
                                : history.createdAt,
                              "hh:mm a dd/MM/yyyy"
                            )}
                          </Col>
                          <Col className="col-auto text-blue-7 font-weight-400 font-size-10">
                            {history.purchaseId}
                          </Col>
                        </Row>
                      </Col>
                      <Col
                        className={`col-auto rounded-5 py-1 font-weight-400 font-size-12 d-flex align-items-center ${getStatusColor(
                          history.solarEstimation.status
                        )}`}
                      >
                        {getStatusNameUser(history.solarEstimation.status)}
                      </Col>
                    </Row>
                  </Col>
                  <Col className="col-auto">
                    <Row className="align-items-center">
                      <Col className="col-auto text-dark-4 font-weight-500 font-size-20">
                        {history.solarEstimation.currency}&nbsp;
                        {formatWithCommas(
                          Number(
                            Helper.roundTo2(
                              history.solarEstimation.data.outputParams
                                ?.grandTotal || 0
                            )
                          )
                        )}
                      </Col>
                      <Col>
                        <MaterialIcon
                          icon={"keyboard_arrow_up"}
                          size={22}
                          color="#74819F"
                          weight={600}
                          className={`${styles.iconToggle} ${
                            collapsed ? styles.isCollapsed : ""
                          } cursor-pointer`}
                          onClick={() => {
                            setCollapsed((ps) => ({
                              ...ps,
                              [history.Id]: !ps[history.Id],
                            }));
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Collapse in={collapsed[history.Id]}>
                  <div
                    className={`bg-white p-3 mt-4 ${styles.purchaseHistoryModalContainer}`}
                  >
                    <Row className="align-items-center justify-content-between">
                      <Col className="text-light font-weight-500 font-size-14">
                        Solar Power Equipments
                      </Col>
                      <Col className="col-auto">
                        <MaterialIcon
                          icon={"keyboard_arrow_up"}
                          size={22}
                          color="#74819F"
                          weight={600}
                          className={`${styles.iconToggle} ${
                            collapsedComponents ? styles.isCollapsed : ""
                          } cursor-pointer`}
                          onClick={() => {
                            setCollapsedComponents((ps) => ({
                              ...ps,
                              [history.solarEstimation.id]:
                                !ps[history.solarEstimation.id],
                            }));
                          }}
                        />
                      </Col>
                    </Row>
                    <Collapse
                      in={collapsedComponents[history.solarEstimation.id]}
                    >
                      <div>
                        {(
                          history.solarEstimation.data.adminSelectedProducts ??
                          history.solarEstimation.data.userSelectedProducts
                        )?.map((usp, index) => {
                          let productDetails:
                            | IViewSolarEstimationSolarProductAndOtherDetails
                            | undefined;
                          const recommendedProducts =
                            history.solarEstimation.data.recommendedProducts;

                          switch (usp.category) {
                            case EViewSolarEstimationSolarProductCategory.INVERTERS:
                              productDetails =
                                recommendedProducts?.INVERTERS.find(
                                  (inverter) => inverter.product.id === usp.id
                                );
                              break;
                            case EViewSolarEstimationSolarProductCategory.BATTERIES:
                              productDetails =
                                recommendedProducts?.BATTERIES.find(
                                  (battery) => battery.product.id === usp.id
                                );
                              break;
                            case EViewSolarEstimationSolarProductCategory.PANELS:
                              productDetails = recommendedProducts?.PANELS.find(
                                (panel) => panel.product.id === usp.id
                              );
                              break;
                          }

                          if (!productDetails) return null;

                          const product = productDetails.product;
                          const count = productDetails.count;

                          return (
                            <Row key={`` + index + usp.id} className="mt-2">
                              <Col>
                                <PurchaseSolarComponent
                                  product={product}
                                  count={count}
                                />
                              </Col>
                            </Row>
                          );
                        })}
                        <Row className="mt-5 px-4">
                          <Col className="text-end">
                            <Row>
                              <Col className="text-secondary font-size-14">
                                Components Cost
                              </Col>
                              <Col
                                xl={4}
                                className="text-dark-4 font-weight-500"
                              >
                                {history.solarEstimation.currency}&nbsp;
                                {formatWithCommas(
                                  Number(
                                    Helper.roundTo2(
                                      history.solarEstimation.data.outputParams
                                        ?.totalCost || 0
                                    )
                                  )
                                )}
                              </Col>
                            </Row>
                            {history.solarEstimation
                              .installationCostRequested && (
                              <Row>
                                <Col className="text-secondary font-size-14">
                                  Installation Cost
                                </Col>
                                <Col
                                  xl={4}
                                  className="text-dark-4 font-weight-500"
                                >
                                  {history.solarEstimation.currency}&nbsp;
                                  {formatWithCommas(
                                    Number(
                                      Helper.roundTo2(
                                        history.solarEstimation.data
                                          .outputParams?.laborCost || 0
                                      )
                                    )
                                  )}
                                </Col>
                              </Row>
                            )}
                            {history.solarEstimation
                              .installationCostRequested && (
                              <Row>
                                <Col className="text-secondary font-size-14">
                                  Accessories Cost
                                </Col>
                                <Col
                                  xl={4}
                                  className="text-dark-4 font-weight-500"
                                >
                                  {history.solarEstimation.currency}&nbsp;
                                  {formatWithCommas(
                                    Number(
                                      Helper.roundTo2(
                                        history.solarEstimation.data
                                          .outputParams?.totalAccessoriesCost ||
                                          0
                                      )
                                    )
                                  )}
                                </Col>
                              </Row>
                            )}
                            <Row className="mt-2">
                              <Col className="text-secondary">Total</Col>
                              <Col
                                xl={4}
                                className="text-dark-4 font-size-20 font-weight-600"
                              >
                                {history.solarEstimation.currency}&nbsp;
                                {formatWithCommas(
                                  Number(
                                    Helper.roundTo2(
                                      history.solarEstimation.data.outputParams
                                        ?.grandTotal || 0
                                    )
                                  )
                                )}
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </Collapse>
                  </div>
                </Collapse>
              </div>
            );
          })
        ) : (
          <div className="container-dash mt-4 text-center  text-color-2 font-size-12">
            There are no purchase histories.
          </div>
        )}
        <SpinnerModal
          show={isFetchingPurchaseHistoriesAdmin || isFetchingPurchaseHistories}
        />
      </>
    </ModalContainer>
  );
};

export default PurchaseHistoryModal;
